/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */

import { Injectable } from '@angular/core';
import { ProjectUsersModel } from '@app/models/project-users.model';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class ProjectUsersListService extends ApiBaseService {
  private _projectUsersList: ProjectUsersModel[] = [];
  protected _endpoint = 'project_users';

  constructor(http: HttpClient, authUserService: AuthUserService) {
    super(http, authUserService);
  }

  get projectUsersList(): ProjectUsersModel[] {
    return this._projectUsersList;
  }

  set projectUsersList(value: ProjectUsersModel[]) {
    this._projectUsersList = value;
  }

  getUsersFromList() {
    if (this.projectUsersList.length) {
      this.dataSubject.next(this.projectUsersList);
    } else {
      this.getData({});
    }
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getProjectUsersById
   *
   * : Get an ProjectUsers based on the id by checking the list
   *
   * @param id of type string
   *
   * @returns Void: gets the project-analytics with the id that meets the query
   */
  getProjectUsersById(projectId: string): ProjectUsersModel {
    for (const project of this.projectUsersList) {
      if (project.projectId === projectId) {
        return project;
      }
    }
    return null;
  }
  /**
   *  Ryan Duffy
   *
   *  getMultipleProjectUsersById
   *
   * : Get an ProjectUsers based on the id by checking the list
   *
   * @param id of type string
   *
   * @returns Void: gets the project-analytics with the id that meets the query
   */
  getMultipleProjectUsersById(projectId: string): ProjectUsersModel[] {
    const projectUsers: ProjectUsersModel[] = [];
    for (const project of this.projectUsersList) {
      if (project.projectId === projectId) {
        projectUsers.push(project);
      }
    }
    return projectUsers;
  }
  /**
   *  Ryan Duffy
   *
   *  getProjectUserByIdAndProjectId
   *
   * : Get an ProjectUsers based on the id and by the projectId by checking the list
   *
   * @param id of type string
   *
   * @returns Void: gets the project-analytics with the id that meets the query
   */
  getProjectUserByIdAndProjectId(projectId: string, userId: string): ProjectUsersModel {
    for (const project of this.projectUsersList) {
      if (project.projectId === projectId && project.userId === userId) {
        return project;
      }
    }
    return null;
  }
  /**
   *   Warren Dowey & Ryan Duffy
   *
   *  addtoList
   *
   * : handles the insert of a new project-analytics into the list
   *
   * @param data of type ProjectUsersModel
   *
   * @returns Void: inserts a new project-analytics into the lsit
   */
  addToList(data: ProjectUsersModel): void {
    this.projectUsersList.push(data);
  }
  /**
   *   Warren Dowey & Ryan Duffy
   *
   *  removeFromList
   *
   * : deletes an project-analytics from the list
   *
   * @param data of type ProjectUsersModel
   *
   * @returns Void: deletes a project-analytics
   */
  removeFromList(data: ProjectUsersModel): void {
    let index = 0;
    if (data) {
      for (const projectUser of this.projectUsersList) {
        if (projectUser.projectId === data.projectId && projectUser.userId === data.userId) {
          this.projectUsersList.splice(index, 1);
        }
        index++;
      }
    }
  }
  /**
   *  Warren Dowey
   *
   *  updateInList
   *
   * : handles the update of an project-analytics in the list
   *
   * @param data of type ProjectUsersModel
   *
   * @returns Void: update the list with the data
   */
  updateInList(data: ProjectUsersModel): void {
    for (let project of this.projectUsersList) {
      if (project.projectId === data.projectId && data.userId === project.userId) {
        for (const property in project) {
          if (project[property] !== data[property]) {
            project[property] = data[property];
          }
        }
      }
    }
  }
  /**
   *  Warren Dowey
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type ProjectUsersModel
   *
   * @returns Void: get the project-analytics from the postgres database and create the list
   */
  protected handleData(data: any): void {
    this.projectUsersList = [];
    let project: ProjectUsersModel;
    super.handleData(data);
    for (const item of data) {
      project = new ProjectUsersModel();
      this.addToList(project.fromJson(item));
    }
  }
}
